import { useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { useAddressRecord, useProjectRecord } from 'src/graphql';
import { mapClient } from 'src/graphql/entities/client';
import { EntityNames, QueryVariables } from 'src/graphql/types';
import { useSelectedConversation } from 'src/pages/Conversations';
import { GetClientsQuery, SEARCH_CLIENTS_QUERY } from './search-client';
import { SearchEntityReturnType } from './use-search';

/**
 * Custom hook to get a single record of different entities.
 *
 * Since React hooks cannot be conditionally rendered, all hooks are initialized and only the
 * selected entity's hook is active while others are disabled, this allows use to get a record of generic entity
 */
export const useSelectedRecord = ({
  recordId: rawRecordId,
  entityName,
}: {
  recordId?: string;
  entityName?: EntityNames;
}) => {
  const recordId = rawRecordId ?? '';

  const { selectedConversation: conversation } = useSelectedConversation({
    conversationId: entityName === EntityNames.Conversation ? recordId : '',
  });

  const { address: site } = useAddressRecord({
    addressId: entityName === EntityNames.Site ? recordId : '',
  });

  const clientId = entityName === EntityNames.Client ? recordId : '';

  const { data: clientData, previousData: prevClientData } = useQuery<
    GetClientsQuery,
    QueryVariables
  >(SEARCH_CLIENTS_QUERY, {
    variables: {
      params: {
        filters: [{ attribute: 'clientId', operator: '=', value: clientId }],
        includeDeleted: true,
      },
    },
    skip: !clientId,
  });

  const client = useMemo(() => {
    const record = (clientData || prevClientData)?.getClient?.records?.find(
      (c) => c.clientId === clientId,
    );
    return clientId && record ? mapClient(record) : undefined;
  }, [clientData, prevClientData, clientId]);

  const project = useProjectRecord({
    projectId: entityName === EntityNames.Project ? recordId : '',
  });

  const data = {
    [EntityNames.Conversation]: conversation,
    [EntityNames.Client]: client,
    [EntityNames.Site]: site,
    [EntityNames.Project]: project,
    [EntityNames.PlatformUser]: null,
  };

  if (!entityName) {
    return null;
  }

  return (data[entityName] as SearchEntityReturnType[EntityNames]) ?? null;
};
